import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight } from 'lucide-react';
import Icon from '../components/Icon';
import Reveal from '../components/Reveal';
import SectorNav from '../components/SectorNav';
import useSeo from '../hooks/useSeo';
import { IMAGES, INVEST_APPROACH, INVEST_SECTORS, INVEST_STATS } from '../mock';

const OurInvestments = () => {
  useSeo({
    title: 'Our Investments | Duffy Investment Group',
    description: "Explore Duffy Investment Group's investment interests across technology, property, gaming, entertainment and other sectors and business opportunities.",
    keywords: 'Duffy Investment Group investments, private investments, technology investment, property investment, gaming investment, entertainment investment, investment opportunities',
  });

  return (
    <div>
      {/* ===================== HERO ===================== */}
      <section className="relative bg-navy-grad overflow-hidden">
        <div className="absolute inset-0 lg:left-[38%]">
          <img src={IMAGES.circuit} alt="Technology investments" className="w-full h-full object-cover" style={{ filter: 'saturate(1.15) brightness(1.08) contrast(1.05)' }} />
          <div className="absolute inset-0 lg:hidden" style={{ background: 'linear-gradient(180deg, rgba(7,19,34,0.35) 0%, rgba(7,19,34,0.55) 45%, rgba(7,19,34,0.9) 100%)' }} />
          <div className="absolute inset-0 hidden lg:block" style={{ background: 'linear-gradient(90deg, rgba(10,26,48,1) 0%, rgba(10,26,48,0.9) 10%, rgba(10,26,48,0.35) 30%, rgba(10,26,48,0) 48%)' }} />
        </div>
        <div className="relative dig-container">
          <div className="flex items-center" style={{ minHeight: 560 }}>
            <div className="pt-12 pb-16 lg:py-24 max-w-xl">
              <Reveal>
                <h1 className="font-serif-dig text-white" style={{ fontSize: 'clamp(1.7rem, 5.6vw, 3.2rem)', fontWeight: 700, lineHeight: 1.12 }}>
                  STRATEGIC INVESTMENTS.<br /><span className="gold-text">REAL WORLD RESULTS.</span>
                </h1>
                <span className="gold-rule-lg block my-7" />
              </Reveal>
              <Reveal delay={140}>
                <p className="font-body" style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1.05rem', lineHeight: 1.75 }}>
                  Duffy Investment Group (DIG) operates a private boutique venture capital investment portfolio, utilizing the extensive experience of our key executives across a diverse range of industries.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTOR NAV (bottom of hero) ===== */}
      <SectorNav />

      {/* ===================== APPROACH ===================== */}
      <section className="bg-cream">
        <div className="dig-container py-16 md:py-20">
          <Reveal className="flex items-center justify-center gap-5">
            <span className="gold-rule hidden sm:block" />
            <h2 className="section-title text-center" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.1rem)' }}>OUR INVESTMENT APPROACH</h2>
            <span className="gold-rule hidden sm:block" />
          </Reveal>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-10 mt-14">
            {INVEST_APPROACH.map((a, i) => (
              <Reveal key={a.title} delay={i * 80} className="flex md:flex-col md:items-center md:text-center gap-4">
                <Icon name={a.icon} size={44} strokeWidth={1.3} style={{ color: 'var(--gold)', flexShrink: 0 }} />
                <div>
                  <h3 className="font-serif-dig navy-text" style={{ fontWeight: 700, fontSize: '1.05rem', letterSpacing: '0.02em' }}>{a.title}</h3>
                  <p className="font-body mt-2" style={{ color: 'var(--muted)', lineHeight: 1.6, fontSize: '0.95rem' }}>{a.text}</p>
                </div>
                <ChevronRight className="md:hidden shrink-0 ml-auto self-center" size={22} style={{ color: 'var(--gold)' }} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== SECTORS (DESKTOP) ===================== */}
      <section className="bg-cream-2 hidden md:block">
        <div className="dig-container py-16 md:py-20">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
            {INVEST_SECTORS.map((s, i) => (
              <Reveal key={s.title} delay={i * 80}>
                <Link to={s.to} data-testid={`sector-tile-${s.title.toLowerCase().replace(/ /g, '-')}`} className="block h-full">
                  <div className="overflow-hidden group h-full flex flex-col" style={{ background: '#0b1b33', border: '1px solid rgba(200,162,76,0.18)' }}>
                    <div className="overflow-hidden">
                      <img src={IMAGES[s.img]} alt={s.title} className="w-full h-auto block transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="px-4 pt-3 pb-5 text-center flex-1 flex flex-col">
                      <p className="font-body" style={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.85rem', lineHeight: 1.5 }}>{s.text}</p>
                      <span className="link-gold justify-center mt-3">LEARN MORE <ArrowRight size={14} /></span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== GLOBAL BAND ===================== */}
      <section className="bg-navy">
        <div className="dig-container py-10 md:py-12">
          <div className="flex flex-row items-center gap-4 md:gap-6 max-w-4xl">
            <Icon name="Globe" size={48} strokeWidth={1} style={{ color: 'var(--gold)', flexShrink: 0 }} />
            <div>
              <h3 className="font-serif-dig gold-text" style={{ fontSize: 'clamp(1.15rem, 4vw, 1.4rem)', fontWeight: 700, lineHeight: 1.2 }}>GLOBAL PERSPECTIVE. LOCAL EXPERTISE.</h3>
              <p className="font-body mt-2" style={{ color: 'rgba(255,255,255,0.82)', lineHeight: 1.6, fontSize: '0.95rem' }}>
                Our investments span a global network of opportunities. We combine international outlook with local knowledge to identify, invest and create long-term value.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== STATS ===================== */}
      <section style={{ background: 'var(--navy-deep)' }}>
        <div className="dig-container py-10 md:py-12">
          <div className="grid grid-cols-4">
            {INVEST_STATS.map((s, i) => (
              <div
                key={s.label}
                className={`flex flex-col items-center text-center md:flex-row md:items-start md:text-left gap-2 md:gap-3 px-1.5 md:px-6 ${i > 0 ? 'border-l' : ''}`}
                style={{ borderColor: 'rgba(255,255,255,0.14)' }}
              >
                <Icon name={s.icon} size={30} strokeWidth={1.2} style={{ color: 'var(--gold)', flexShrink: 0 }} />
                <div>
                  <p className="font-serif-dig gold-text" style={{ fontWeight: 800, lineHeight: 1.15 }}>
                    <span className="md:hidden" style={{ fontSize: '1.02rem', whiteSpace: 'nowrap' }}>{s.mValue}</span>
                    <span className="hidden md:inline" style={{ fontSize: '1.05rem' }}>{s.value}</span>
                  </p>
                  <p className="font-label" style={{ color: 'rgba(255,255,255,0.78)', fontWeight: 600, letterSpacing: '0.06em', marginTop: 4 }}>
                    <span className="md:hidden" style={{ fontSize: '0.56rem' }}>{s.mLabel}</span>
                    <span className="hidden md:inline" style={{ fontSize: '0.64rem' }}>{s.label}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurInvestments;
